import type { Event } from "@/services/api/events";
import type { StatusTone } from "@/components/StatusBadge/StatusBadge";
import { env } from "@/lib/utils";
import { formatDate } from "@/lib/dateFormat";
import { formatINR } from "@/lib/currency";

export const EVENT_TYPE_LABELS: Record<Event["eventType"], string> = {
  MARQUEE: "The Marquee",
  MICRO_MEETUPS: "Micro-meetup",
  MASTERCLASS: "Masterclass",
};

// Shown in the viewer's local timezone, like every date in the app (see
// lib/dateFormat.ts) — e.g. "…T12:00:00Z" displays as 5:30 PM in India.
export const formatEventDate = (iso: string | null | undefined) => formatDate(iso, { month: "long" });

export const formatEventTime = (iso: string | null | undefined) =>
  formatDate(iso, { locale: "en-US", hour: "numeric", minute: "2-digit", hour12: true });

export const formatTicketPrice = (price: number | null) => (price && price > 0 ? formatINR(price) : "Included");

// Only "PUBLISHED" and `null` have been observed on a real response — see
// services/api/events.ts. `null` is treated as "not yet published" (Draft).
export const getPublishingLabel = (status: string | null) => (status === "PUBLISHED" ? "Published" : "Draft");

export const GATHERING_STATUS_TONE: Record<string, StatusTone> = {
  Published: "success",
  Draft: "neutral",
};

// `thumbnailUrl` comes back as a relative path (e.g.
// "files/thumbnail/<uuid>_name.png"), served by the backend under `/api/`.
const THUMBNAIL_BASE_URL = `${env("VITE_API_BASE_URL")}/api/`;

export const resolveThumbnailUrl = (thumbnailUrl: string | null) => {
  if (!thumbnailUrl) return null;
  return `${THUMBNAIL_BASE_URL}${thumbnailUrl}`;
};

// `cities` (the originally guessed city field) came back `null` on a real
// sample where `locations[0].cityName` carried the actual value — see
// `Event.locations`'s own comment in services/api/events.ts. `cities?.[0]`
// is kept as a fallback rather than dropped outright, in case some other
// response shape (e.g. a multi-city event) still populates it instead.
export const getEventCityName = (event: Event) => event.locations?.[0]?.cityName || event.cities?.[0];

export const isUpcoming = (event: Event, today: Date) => {
  const eventDate = new Date(event.eventDate);
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return eventDate >= todayMidnight;
};
